<script setup lang="ts">
import type { VaultBrowserItem } from '@/components/file_vault/vaultFileBrowser';
import { nextTick, ref, watch } from 'vue';
import { gaiaFontSize, gaiaNeutral } from '@/components/design/themes/tokens';
import { NButton, NIcon, NInput, NModal, NSpace, type InputInst } from 'naive-ui';
import { IconAdd, IconCheck } from '@/components/design/icons';
import VaultFileBrowser from '@/components/file_vault/VaultFileBrowser.vue';
import { useVaultStore } from '@/stores/vaultStore';
import { extractErrorDetail } from '@/utils/errorHandler';
import { useGaiaToast } from '@/composables/useGaiaToast';

interface DirectoryPickerSelection {
  uid: string;
  label: string;
  breadcrumb_labels: string[];
}

interface VaultBrowserInstance {
  getCurrentDirectoryContext(): { uid: string | null; label: string };
  getCurrentBreadcrumbs(): { uid: string | null; label: string }[];
  openDirectory(uid: string, label: string): Promise<void>;
}

interface Props {
  show: boolean;
  /** Currently selected destination — used as the default highlight. */
  initialUid?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  initialUid: null,
});

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void;
  (e: 'select', value: DirectoryPickerSelection): void;
}>();

const browserRef = ref<VaultBrowserInstance | null>(null);
const message = useGaiaToast();
const vaultStore = useVaultStore();

function disableFileSelection(
  item: VaultBrowserItem
): { disabled?: boolean; reason?: string | null } | null {
  if (item.type === 'file') {
    return {
      disabled: true,
      reason: 'Files are not a valid destination — navigate into a folder.',
    };
  }
  return null;
}

function close(): void {
  emit('update:show', false);
}

function confirmSelection(): void {
  const context = browserRef.value?.getCurrentDirectoryContext();
  const crumbs = browserRef.value?.getCurrentBreadcrumbs() ?? [];
  if (!context?.uid) {
    message.warning('Navigate into a folder first, then click "Use this folder".');
    return;
  }
  emit('select', {
    uid: context.uid,
    label: context.label,
    breadcrumb_labels: crumbs.map(node => node.label),
  });
  close();
}

// --- New folder -----------------------------------------------------------
//
// Somewhere to put the document may not exist yet, and leaving to make it in
// the vault would lose the draft's place. The folder is created where the
// browser currently stands, and we then walk into it — creating a folder here
// only ever means "save into this".

const creatingFolder = ref<boolean>(false);
const newFolderName = ref<string>('');
const savingFolder = ref<boolean>(false);
const nameInput = ref<InputInst | null>(null);

/**
 * The folder the new one goes into, captured when the form opens rather than
 * read at submit: the row says "New folder in X", and a form that promises X
 * has to create it in X even if the browser wandered off meanwhile.
 */
const createParent = ref<{ uid: string | null; label: string }>({ uid: null, label: 'My Vault' });

function openCreateForm(): void {
  createParent.value = browserRef.value?.getCurrentDirectoryContext() ?? {
    uid: null,
    label: 'My Vault',
  };
  newFolderName.value = '';
  creatingFolder.value = true;
  void nextTick(() => nameInput.value?.focus());
}

function cancelCreate(): void {
  creatingFolder.value = false;
  newFolderName.value = '';
}

async function createFolder(): Promise<void> {
  const name = newFolderName.value.trim();
  if (name.length === 0 || savingFolder.value) return;

  savingFolder.value = true;
  try {
    const created = await vaultStore.createDirectory({
      name,
      parent_directory_uid: createParent.value.uid ?? undefined,
    });
    cancelCreate();
    // Land inside it, so "Use this folder" is already the right answer.
    await browserRef.value?.openDirectory(created.uid, created.directory_name);
    message.success(`“${created.directory_name}” created in ${createParent.value.label}.`);
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to create the folder.'));
  } finally {
    savingFolder.value = false;
  }
}

watch(
  () => props.show,
  show => {
    if (!show) return;
    cancelCreate();
    void nextTick();
  }
);

/**
 * Inline custom properties rather than `v-bind()` — the modal root is a
 * Teleport, so Vue has no root element in place to write `v-bind()` vars onto.
 */
const styleVars = {
  '--dirpick-fs-row': gaiaFontSize.row,
  '--dirpick-subtle': gaiaNeutral.textSubtle,
};
</script>

<template>
  <n-modal
    :show="show"
    preset="card"
    title="Choose destination folder"
    :style="[styleVars, { width: '720px', maxWidth: 'calc(100vw - 32px)' }]"
    :mask-closable="false"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <!-- The hint row becomes the create row, so naming a folder never grows
         the modal or leaves two instructions competing. -->
    <n-flex
      v-if="!creatingFolder"
      align="center"
      justify="space-between"
      :size="12"
      :wrap="false"
      style="margin: 0 0 12px"
    >
      <p class="picker-hint">
        Browse the vault, navigate into the folder you want to save the document into, then click
        <strong>Use this folder</strong>.
      </p>
      <n-button size="small" @click="openCreateForm">
        <template #icon>
          <n-icon :component="IconAdd" />
        </template>
        New folder
      </n-button>
    </n-flex>

    <n-flex v-else align="center" :size="8" :wrap="false" style="margin: 0 0 12px">
      <span class="picker-hint" style="white-space: nowrap">
        New folder in <strong>{{ createParent.label }}</strong>
      </span>
      <n-input
        ref="nameInput"
        v-model:value="newFolderName"
        size="small"
        placeholder="Folder name"
        :disabled="savingFolder"
        style="flex: 1; min-width: 0"
        @keyup.enter="createFolder"
      />
      <n-button size="small" :disabled="savingFolder" @click="cancelCreate">Cancel</n-button>
      <n-button
        size="small"
        type="primary"
        :loading="savingFolder"
        :disabled="newFolderName.trim().length === 0"
        @click="createFolder"
      >
        Create
      </n-button>
    </n-flex>

    <div class="picker-browser">
      <VaultFileBrowser
        ref="browserRef"
        :selected-keys="[]"
        :single-selection="true"
        :get-item-selection-state="disableFileSelection"
      />
    </div>

    <template #footer>
      <n-space justify="end">
        <n-button @click="close">Cancel</n-button>
        <n-button type="primary" @click="confirmSelection">
          <template #icon>
            <n-icon :component="IconCheck" />
          </template>
          Use this folder
        </n-button>
      </n-space>
    </template>
  </n-modal>
</template>

<style scoped>
.picker-hint {
  margin: 0;
  font-size: var(--dirpick-fs-row);
  color: var(--dirpick-subtle);
}

/* No frame of its own: the browser draws its list's border already. */
.picker-browser {
  height: 480px;
}
</style>
